import { useCallback, useEffect, useState } from "react";

import useUserGuideGroup from "shared/UserGuides/useUserGuideGroup";
import useUserGuideCertificates from "shared/UserGuides/useUserGuideCertificates";
import { showCongratsModal } from "views/Account/LaunchPad/UserGuidesGroup/SidePanel/CongratsModal";

import useAssistantPanelLearnStore from "../../useStore";
import useAssistantPanelLearnActiveGuide from "../../useActiveGuide";
import { CompletedGuideData } from "../../types";
import AssistantPanelLearnActiveGuideViewSkeleton from "./Skeleton";
import AssistantPanelLearnActiveGuideViewStep from "./Step";
import AssistantPanelLearnActiveGuideViewCompletionStep from "./CompletionStep";

const AssistantPanelLearnActiveGuideView = () => {
  const { setNavigationState, navigationState } = useAssistantPanelLearnStore();
  const {
    activeGuide,
    currentStep,
    currentStepIndex,
    totalSteps,
    loading,
    nextGuideId,
    isNextGuideCompleted,
  } = useAssistantPanelLearnActiveGuide();

  const groupId = "groupId" in navigationState ? navigationState.groupId : undefined;
  const { userGuideGroup } = useUserGuideGroup({ id: groupId ?? "" });
  const { getCertificateByGroupId } = useUserGuideCertificates();

  const [justCompletedGuide, setJustCompletedGuide] = useState<CompletedGuideData | undefined>();

  useEffect(() => {
    if (!loading && !activeGuide && !justCompletedGuide) {
      setNavigationState({ level: "groups" });
    }
  }, [loading, activeGuide, justCompletedGuide, setNavigationState]);

  const handleOnGuideCompleteClick = useCallback(() => {
    if (!activeGuide) return;

    setJustCompletedGuide({
      guideTitle: activeGuide.metadata.title,
      completionMessage: activeGuide.completion.successMessage,
      nextGuideId,
      totalSteps,
      isNextGuideCompleted,
    });
  }, [activeGuide, totalSteps, nextGuideId, isNextGuideCompleted]);

  const handleOnGuideCompleteSuccess = useCallback(() => {
    if (!userGuideGroup || !groupId || nextGuideId) return;

    const certificate = getCertificateByGroupId(groupId);
    if (certificate) return;

    const { guidesCount, guidesCompletedCount, name } = userGuideGroup;

    if (guidesCompletedCount + 1 >= guidesCount) {
      showCongratsModal({ groupId, groupName: name });
    }
  }, [userGuideGroup, groupId, nextGuideId, getCertificateByGroupId]);

  if (loading && !activeGuide) {
    return <AssistantPanelLearnActiveGuideViewSkeleton />;
  }

  if (activeGuide && currentStep) {
    return (
      <AssistantPanelLearnActiveGuideViewStep
        key={activeGuide.id}
        guideTitle={activeGuide.metadata.title}
        currentStep={currentStep}
        currentStepIndex={currentStepIndex}
        totalSteps={totalSteps}
        onCompleteClick={handleOnGuideCompleteClick}
        onCompleteSuccess={handleOnGuideCompleteSuccess}
      />
    );
  }

  if (justCompletedGuide) {
    return <AssistantPanelLearnActiveGuideViewCompletionStep data={justCompletedGuide} />;
  }

  return null;
};

export default AssistantPanelLearnActiveGuideView;
